import {APIClient} from '../utils/APIClient';
import {API_URL} from '../settings/local_settings';

// Biblioteca de prompts (backend: `agent/prompt-library/`).
//
// Acá viven las llamadas que comparten dos superficies o que no son un simple GET de la lista: la
// visibilidad en el portal (card + modal de alta), la edición y la baja. Vivían dentro de
// `PromptLibraryCard.js`, así que el modal importaba un componente para hacer un request.
//
// `portal-visibility` es un endpoint dedicado a propósito: el PUT genérico del prompt NO acepta
// `portal_visible` (contrato acordado con el Backend).

export const PromptLibraryApi = {
    /** Publica (o despublica) un prompt en el portal del cliente. */
    setPortalVisibility(promptId, visible) {
        const url = `${API_URL}agent/prompt-library/${encodeURIComponent(promptId)}/portal-visibility/`;
        return new APIClient(url).call('PUT', {visible});
    },

    /**
     * Edita un prompt. Todos los campos son opcionales y **lo que no se manda no se toca**, así
     * que el payload lleva sólo lo que el modal edita. Cada PUT genera una versión nueva en el
     * backend; `change_description` es la nota que queda en ese historial.
     *
     * Devuelve el `library_data()` actualizado (trae `id`), no el `{status}` de las mutaciones.
     */
    update(promptId, payload) {
        const url = `${API_URL}agent/prompt-library/${encodeURIComponent(promptId)}/`;
        return new APIClient(url).call('PUT', payload);
    },

    /**
     * Desactiva un prompt (soft-delete: `_is_active=False`). Se vuelve con `restore()`, que sólo
     * puede usar quien lo creó (la misma regla que desactivar).
     */
    deactivate(promptId) {
        const url = `${API_URL}agent/prompt-library/${encodeURIComponent(promptId)}/`;
        return new APIClient(url).call('DELETE');
    },

    // --- Pantalla «Prompts» de la agencia (`/agency/prompts`) -----------------------------------
    // Estas vistas responden el payload PELADO, sin clave `status` en el éxito (`Response({...})`,
    // no `OKResponse`): los llamadores deciden por la FORMA de la respuesta (`Array.isArray(r.own)`,
    // `r.id`), nunca por `status === 'OK'`. Contrato: `~/.claude/plans/2026-09-25-galeria-prompts/
    // contrato.md` §1.

    /** La biblioteca a nivel agencia: `{agency, clients, own, inactive_own, dashbo}`. */
    agencyLibrary() {
        return new APIClient(`${API_URL}agent/prompt-library/agency/`).call('GET');
    },

    /**
     * Crea un prompt propio. `momentos` es un ARRAY; `scope` es `AGENCY` o `CLIENT` (con
     * `agency_client_id`). No acepta `portal_visible`: se publica con `setPortalVisibility` después.
     * Devuelve el `library_data()` del prompt nuevo (201).
     */
    create(payload) {
        return new APIClient(`${API_URL}agent/prompt-library/create/`).call('POST', payload);
    },

    /**
     * «Adaptar»: copia editable de un prompt. Sin cliente (`null`) la copia es de toda la agencia;
     * con cliente, de ese cliente. 409 (con `raw_body.fork_id`) si ya existe una copia activa.
     */
    fork(promptId, agencyClientId) {
        const url = `${API_URL}agent/prompt-library/${encodeURIComponent(promptId)}/fork/`;
        return new APIClient(url).call('POST', agencyClientId ? {agency_client_id: agencyClientId} : {});
    },

    /** Reactiva un prompt desactivado. Idempotente; 403 si no lo creó el usuario. */
    restore(promptId) {
        const url = `${API_URL}agent/prompt-library/${encodeURIComponent(promptId)}/restore/`;
        return new APIClient(url).call('POST');
    },

    /** Voto útil/no útil. `value` es `'UP'`, `'DOWN'` o `null` (borra el voto). */
    rate(promptId, value) {
        const url = `${API_URL}agent/prompt-library/${encodeURIComponent(promptId)}/rate/`;
        return new APIClient(url).call('POST', {value});
    },

    /**
     * Ping de uso, fire-and-forget. Sin cliente para un prompt de toda la agencia; con el del
     * prompt para uno de un cliente.
     */
    used(promptId, agencyClientId) {
        const url = `${API_URL}agent/prompt-library/${encodeURIComponent(promptId)}/used/`;
        return new APIClient(url).call('POST', agencyClientId ? {agency_client_id: agencyClientId} : {});
    },
};
